import * as Sentry from "@sentry/react";
import { getApiClient } from "@src/api";
import {
	type NavbarBranchItem,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { graphql } from "@src/gql/gql";
import type { NavbarSection } from "@src/gql/graphql";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const renameFolderMutation = graphql(/* GraphQL */ `
	mutation renameFolder(
		$section: NavbarSection!
		$folderKey: String!
		$newName: String!
	) {
		navbar {
			renameFolder(
				section: $section
				folderKey: $folderKey
				newName: $newName
			) {
				key
				name
				parentKey
				rank
			}
		}
	}
`);

type RenameFolderInput = {
	section: NavbarSection;
	folderKey: string;
	newName: string;
};

export function useRenameFolder() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ section, folderKey, newName }: RenameFolderInput) => {
			const response = await client.mutate({
				mutation: renameFolderMutation,
				variables: { section, folderKey, newName },
			});
			return response.data?.navbar?.renameFolder ?? null;
		},
		onMutate: async ({ section, folderKey, newName }) => {
			await queryClient.cancelQueries({
				queryKey: navbarBranchQueryKeys.section(section),
			});
			const previousBranches = queryClient.getQueriesData<NavbarBranchItem[]>({
				queryKey: navbarBranchQueryKeys.section(section),
			});

			queryClient.setQueriesData<NavbarBranchItem[]>(
				{ queryKey: navbarBranchQueryKeys.section(section) },
				(old) => {
					if (!old) {
						return old;
					}
					if (!old.some((item) => item.key === folderKey)) {
						return old;
					}
					return old.map((item) =>
						item.key === folderKey ? { ...item, name: newName } : item,
					);
				},
			);

			const affectedBranchKeys = previousBranches
				.filter(([, data]) => data?.some((item) => item.key === folderKey))
				.map(([queryKey]) => queryKey);

			return { previousBranches, affectedBranchKeys };
		},
		onError: (error, _variables, context) => {
			if (context?.previousBranches) {
				for (const [queryKey, data] of context.previousBranches) {
					queryClient.setQueryData(queryKey, data);
				}
			}
			Sentry.captureException(error);
		},
		onSettled: (_data, _error, _variables, context) => {
			for (const queryKey of context?.affectedBranchKeys ?? []) {
				queryClient.invalidateQueries({ queryKey });
			}
		},
	});
}
